import type { CvtConcernOption } from './cvt-primary-concerns-modal.types';

import { Button, Icon, Typography } from '@packages/ui/shared';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { CvtConcernsMultiSelect } from './cvt-concerns-multi-select';

export type SelectConcernsProps = {
    /** The concerns the user can select. */
    concernOptions: CvtConcernOption[];

    /** Continues with the selected concerns, which decide what the modal shows next. */
    onContinue: () => void;

    /** Called with the selected concerns whenever one is added or removed. */
    onSelectedConcernOptionsChange: (selectedConcernOptions: CvtConcernOption[]) => void;

    /** The selected concerns, shown as chips in the concerns multi-select. */
    selectedConcernOptions: CvtConcernOption[];

    /** Takes the user back to where they came from. Without it, no Back button shows. */
    onBack?: () => void;
};

/**
 * The content the CVT primary concerns modal opens on, allowing the user to select their concerns
 * for the vehicle.
 *
 * Renders the concerns multi-select, a Back button when `onBack` is given, and a Continue button that stays disabled
 * until a concern is selected.
 */
export function SelectConcerns({
    concernOptions,
    onContinue,
    onSelectedConcernOptionsChange,
    selectedConcernOptions,
    onBack,
}: SelectConcernsProps) {
    const { t } = useTranslation();

    const isContinueButtonDisabled = selectedConcernOptions.length === 0;
    const questionLabel = t('cvt:selectConcernsModal.question', 'Which concerns are you seeing on this vehicle?');

    return (
        <>
            <Modal.Header closeButton>
                <Modal.Title>{t('cvt:selectConcernsModal.title', 'Run CVT Diagnostic')}</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <div className="d-flex flex-column gap-3">
                    <Typography>{questionLabel}</Typography>
                    <CvtConcernsMultiSelect
                        ariaLabel={questionLabel}
                        onChange={onSelectedConcernOptionsChange}
                        options={concernOptions}
                        placeholder={t('cvt:selectConcernsModal.placeholder', 'Select all that apply')}
                        placeholderWhileMenuOpen={t(
                            'cvt:selectConcernsModal.placeholderWhileMenuOpen',
                            'Select all that apply to this vehicle'
                        )}
                        selectedOptions={selectedConcernOptions}
                    />
                </div>
            </Modal.Body>

            <Modal.Footer>
                {onBack && (
                    <div className="me-auto">
                        <Button
                            iconLeft={<Icon color="blueOne" name="chevron-left" size={18} />}
                            onPress={onBack}
                            variant="ghost-blue"
                        >
                            {t('buttons:back', 'Back')}
                        </Button>
                    </div>
                )}

                <Button disabled={isContinueButtonDisabled} onPress={onContinue} variant="primary">
                    {t('buttons:continue', 'Continue')}
                </Button>
            </Modal.Footer>
        </>
    );
}
